import { Assert } from "../Assert";
import { PartialNullable, RadPromiseLike, XMLHttpRequestLike } from "../Extensions";
import { ObjectHelper } from "../Helpers/ObjectHelper";
import { PromiseHelper } from "../Helpers/PromiseHelper";
import { StringHelper } from "../Helpers/StringHelper";
import { PropertyChange } from "../PropertyChange";
import { Property, PropertyString } from "../RadObject";
import { AjaxRequest } from "./AjaxRequest";
import { HttpHeaderManager } from "./HttpHeaderManager";
import { IAjaxRequest } from "./IAjaxRequest";
import { IODataBatchRequest } from "./IODataBatchRequest";
import { ODataBatchRequestPropertyValues } from "./ODataBatchRequestPropertyValues";
import { ServerErrorHelper } from "./ServerErrorHelper";
import { NAMESPACE } from "./_Namespace";

export class ODataBatchRequest extends AjaxRequest implements IODataBatchRequest
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ODataBatchRequest";
    public static override className = "ODataBatchRequest";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ODataBatchRequestPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                requests: [],
                type: "BATCH",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected httpHeaderManager = new HttpHeaderManager();

    // #endregion

    // #region Properties

    public baseUrl!: PropertyString;
    public requests!: Property<IAjaxRequest[]>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ODataBatchRequestPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Protected Methods

    protected _baseUrlChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _createBatchBody(requests: IAjaxRequest[], boundary: string)
    {
        const
            body = [],
            baseUrl = this.baseUrl.get();

        let
            changesetBoundary = null;

        Assert.isNotNullOrUndefined(baseUrl, "baseUrl");

        for (let i = 0; i < requests.length; i++)
        {
            const
                request = requests[i],
                settings = request.settings.get(),
                type = settings.type.toUpperCase();

            if (!changesetBoundary)
            {
                switch (type)
                {
                    case "DELETE":
                    case "MERGE":
                    case "PATCH":
                    case "POST":
                    case "PUT":

                        if (i === 0)
                            body.push(`--${boundary}`);

                        changesetBoundary = `changeset_${new Date().getTime()}_${i}`;
                        body.push(`Content-Type: multipart/mixed; boundary=${changesetBoundary}`);
                        body.push(StringHelper.empty);
                        body.push(`--${changesetBoundary}`);
                        break;
                    default:
                        body.push(`--${boundary}`);
                        break;
                }
            }
            else
            {
                switch (type)
                {
                    case "DELETE":
                    case "MERGE":
                    case "PATCH":
                    case "POST":
                    case "PUT":
                        body.push(`--${changesetBoundary}`);
                        break;
                    default:
                        body.push(`--${changesetBoundary}--`);
                        changesetBoundary = null;
                        break;
                }
            }

            body.push("Content-Type: application/http");
            body.push("Content-Transfer-Encoding: binary");
            body.push(StringHelper.empty);

            body.push(`${type} ${settings.url} HTTP/1.1`);

            body.push(`Host: ${location.host}`);
            body.push("Content-Type: application/json");
            body.push("Prefer: return=representation");
            body.push(`Content-ID: ${i}`);

            if (!settings.data)
                body.push(StringHelper.empty);
            else
            {
                body.push(`Content-Length: ${StringHelper.byteLength(settings.data)}`);
                body.push(StringHelper.empty);

                body.push(settings.data);
                body.push(StringHelper.empty);
            }
        }

        if (changesetBoundary)
        {
            body.push(`--${changesetBoundary}--`);
            changesetBoundary = null;
        }

        body.push(`--${boundary}--`);

        return body.join("\r\n");
    }
    protected _createDoneData(responses: XMLHttpRequestLike[])
    {
        const
            requests = this.requests.get() ?? [];

        if (requests.length !== responses.length)
            throw new Error("The number of responses does not match the number of requests");

        const
            data = [];

        for (let i = 0; i < requests.length; i++)
        {
            data.push({
                request: requests[i],
                response: responses[i]
            });
        }

        return data;
    }
    protected _parseBatchResponse(batchResponse: string)
    {
        batchResponse = batchResponse.replace(/\r\n/gi, "\n");

        const
            indexOfLineBreak = batchResponse.indexOf("\n"),
            boundary = batchResponse.substring(0, indexOfLineBreak);

        if (!boundary.length)
            throw new Error("Cannot determine boundary.");

        const
            responses = [],
            responseSections = batchResponse.split(boundary);

        for (let i = 0; i < responseSections.length; i++)
        {
            let
                responseSection = StringHelper.trim(responseSections[i], [" ", "\n"]);

            if (!responseSection.length || responseSection === "--")
                continue;

            const
                response: XMLHttpRequestLike = {};

            // Get Request Headers
            let
                indexOfDoubleLineBreak = responseSection.indexOf("\n\n");

            if (indexOfDoubleLineBreak === -1)
                continue;

            const
                requestHeaders = responseSection.substring(0, indexOfDoubleLineBreak);

            responseSection = responseSection.substring(indexOfDoubleLineBreak + 2);

            // Check for Changeset
            this.httpHeaderManager.setHeaderText(requestHeaders);

            const
                contentTypeHeader = this.httpHeaderManager.getHeader("Content-Type");

            if (contentTypeHeader?.value?.toLowerCase() === "multipart/mixed")
            {
                Array.prototype.push.apply(responses, this._parseBatchResponse(responseSection));
                continue;
            }

            responses.push(response);

            // Get Response Headers
            indexOfDoubleLineBreak = responseSection.indexOf("\n\n");

            let
                responseHeaders;

            if (indexOfDoubleLineBreak === -1)
            {
                responseHeaders = responseSection;
                responseSection = StringHelper.empty;
            }
            else
            {
                responseHeaders = responseSection.substring(0, indexOfDoubleLineBreak);
                responseSection = responseSection.substring(indexOfDoubleLineBreak + 2);
            }

            const
                statusMatches = /HTTP\/1.1 ([0-9]+)( (.*))?/g.exec(responseHeaders);

            if (statusMatches)
            {
                response.status = parseInt(statusMatches[1]);

                if (statusMatches.length >= 3)
                    response.statusText = statusMatches[3];
            }

            response.responseText = responseSection;
            response.responseType = "json";

            // Get Json
            try { response.response = ObjectHelper.fromJson(response.responseText); }
            catch (e) { /* Continue regardless of error */ }
        }

        return responses;
    }
    protected _requestsChanged(change: PropertyChange<IAjaxRequest[]>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ODataBatchRequestPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.baseUrl = new PropertyString(this, propertyValues.baseUrl, this._baseUrlChanged);
        this.requests = new Property<IAjaxRequest[]>(this, "System.Object, System.Private.CoreLib", propertyValues.requests, this._requestsChanged);
    }

    public override executeAsync<TReturn extends any = any>(options?: any): RadPromiseLike<TReturn>
    {
        const
            boundary = `batch_${new Date().getTime()}`,
            settings = ObjectHelper.extend(true, {}, {
                data: this._createBatchBody(this.requests.get() ?? [], boundary),
                contentType: `multipart/mixed; boundary="${boundary}"`
            }, this.settings.get()),
            promise = PromiseHelper.createPromisePublic<any>();

        this._ajax<string>(settings)
            .then(data =>
            {
                const
                    responses = this._parseBatchResponse(data);

                for (let i = 0; i < responses.length; i++)
                {
                    const
                        response = responses[i];

                    if (!response.status || response.status >= 400)
                    {
                        const
                            error = ServerErrorHelper.processError(responses[i]);

                        if (error.type.get() === "ValidationApiException")
                            promise.reject(error);
                        else
                            this._ajaxCallFailed(error, settings, promise);

                        return;
                    }
                }

                promise.resolve(this._createDoneData(responses));
            })
            .catch(error =>
            {
                promise.reject(error);
            });

        return promise;
    }

    // #endregion

    // #endregion
}